import network from '@/services/network';
import {
  CreateSegmentInput,
  FieldOption,
  FieldRegistryItem,
  KontaktTreffer,
  Pagination,
  Segment,
  SegmentFilters,
  SegmentMember,
  UebereinstimmungErgebnis,
} from '@/types';
import { ErrorResponse } from '@/utils';

const getSegments = async (params: {
  page?: number;
  per_page?: number;
  search?: string;
  list_type?: string;
  sort?: string;
  sort_dir?: string;
  ohne_groesse?: 1;
}): Promise<{ data: Segment[]; meta: Pagination }> => {
  try {
    const { data } = await network.get<{ data: Segment[]; meta: Pagination }>(
      '/segments',
      { params },
    );
    return data;
  } catch (error) {
    throw new ErrorResponse(error);
  }
};

/** Größe eines Segments, von der Liste nachgeladen (§8.7 Nr. 2). */
const getGroesse = async (id: number): Promise<{ id: number; size: number | null; size_is_estimate: boolean }> => {
  try {
    const { data } = await network.get<{ data: { id: number; size: number | null; size_is_estimate: boolean } }>(
      `/segments/${id}/groesse`,
    );
    return data.data;
  } catch (error) {
    throw new ErrorResponse(error);
  }
};

const getSegment = async (id: number): Promise<{ data: Segment }> => {
  try {
    const { data } = await network.get<{ data: Segment }>(`/segments/${id}`);
    return data;
  } catch (error) {
    throw new ErrorResponse(error);
  }
};

const createSegment = async (
  input: CreateSegmentInput,
): Promise<{ data: Segment; is_initializing: boolean }> => {
  try {
    const { data } = await network.post<{
      data: Segment;
      is_initializing: boolean;
    }>('/segments', input);
    return data;
  } catch (error) {
    throw new ErrorResponse(error);
  }
};

const updateSegment = async (
  id: number,
  patch: Partial<CreateSegmentInput>,
): Promise<{ data: Segment }> => {
  try {
    const { data } = await network.put<{ data: Segment }>(
      `/segments/${id}`,
      patch,
    );
    return data;
  } catch (error) {
    throw new ErrorResponse(error);
  }
};

const deleteSegment = async (id: number): Promise<void> => {
  try {
    await network.delete(`/segments/${id}`);
  } catch (error) {
    throw new ErrorResponse(error);
  }
};

const getMembers = async (
  segmentId: number,
  params: { page?: number; per_page?: number },
): Promise<{
  data: SegmentMember[];
  meta: { current_page: number; total: number; per_page: number };
}> => {
  try {
    const { data } = await network.get<{
      data: SegmentMember[];
      meta: { current_page: number; total: number; per_page: number };
    }>(`/segments/${segmentId}/members`, { params });
    return data;
  } catch (error) {
    throw new ErrorResponse(error);
  }
};

const exportSegment = async (
  segmentId: number,
  format: 'csv' | 'xlsx',
): Promise<Blob> => {
  try {
    const { data } = await network.get(`/segments/${segmentId}/export`, {
      params: { format },
      responseType: 'blob',
    });
    return data;
  } catch (error) {
    throw new ErrorResponse(error);
  }
};

const getFields = async (
  entityType?: string,
): Promise<{ fields: FieldRegistryItem[] }> => {
  try {
    const params = entityType ? { entity: entityType } : {};
    const { data } = await network.get<{ fields: FieldRegistryItem[] }>(
      '/segment-fields',
      { params },
    );
    return data;
  } catch (error) {
    throw new ErrorResponse(error);
  }
};

const getOptions = async (
  source: string,
): Promise<{ options: FieldOption[] }> => {
  try {
    const { data } = await network.get<{ options: FieldOption[] }>(
      `/segment-options/${source}`,
    );
    return data;
  } catch (error) {
    throw new ErrorResponse(error);
  }
};

/** Zaehlt eine ungespeicherte Definition (POST /segments/vorschau). */
const previewCount = async (
  entityType: string,
  filters: SegmentFilters,
): Promise<number> => {
  try {
    const { data } = await network.post<{ count: number }>(
      '/segments/vorschau',
      { entity_type: entityType, filters },
    );
    return data.count;
  } catch (error) {
    throw new ErrorResponse(error);
  }
};

/**
 * Zaehlt eine ungespeicherte Definition und liefert die ersten Zeilen in den
 * gewaehlten Spalten (Vorschau-Karte, Task segmente-elsbeth-feed §8).
 */
const previewZeilen = async (
  entityType: string,
  filters: SegmentFilters,
  columnConfig: string[],
): Promise<{ count: number; zeilen: SegmentMember[] }> => {
  try {
    const { data } = await network.post<{ count: number; zeilen: SegmentMember[] }>(
      '/segments/vorschau',
      { entity_type: entityType, filters, column_config: columnConfig },
    );
    return { count: data.count, zeilen: data.zeilen ?? [] };
  } catch (error) {
    throw new ErrorResponse(error);
  }
};

/** Kontakt suchen für „Übereinstimmung testen" (Name, Telefon oder E-Mail). */
const kontaktSuche = async (q: string): Promise<KontaktTreffer[]> => {
  try {
    const { data } = await network.get<{ data: KontaktTreffer[] }>('/segments/kontakt-suche', { params: { q } });
    return data.data;
  } catch (error) {
    throw new ErrorResponse(error);
  }
};

/** Entwurf gegen einen Kontakt prüfen (§8.7 Nr. 8) — nur lesend. */
const uebereinstimmung = async (
  entityType: string,
  filters: SegmentFilters,
  kontaktId: number,
): Promise<UebereinstimmungErgebnis> => {
  try {
    const { data } = await network.post<{ data: UebereinstimmungErgebnis }>('/segments/uebereinstimmung', {
      entity_type: entityType,
      filters,
      kontakt_id: kontaktId,
    });
    return data.data;
  } catch (error) {
    throw new ErrorResponse(error);
  }
};

const segmentApi = {
  kontaktSuche,
  uebereinstimmung,
  previewCount,
  previewZeilen,
  getSegments,
  getGroesse,
  getSegment,
  createSegment,
  updateSegment,
  deleteSegment,
  getMembers,
  exportSegment,
  getFields,
  getOptions,
};

export default segmentApi;
